// ✅ src/pages/OrderManagement/ProductionInward.jsx (FULL FILE - UPDATED WITH SAFE LOCALSTORAGE + COMMON HEADERS)

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import ProjectPostOrderLayoutWrapper from "../../components/ProjectPostOrderLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaArrowLeft, FaPaperPlane } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";

const API = "https://nissi-india.com/api/inwardEntry";

const pad2 = (n) => String(n).padStart(2, "0");

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const s = (v) => (v === null || v === undefined ? "" : String(v)).trim();

export default function ProductionInward() {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();
  const location = useLocation();

  // ✅ Safe localStorage getter
  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? String(val).trim() : fallback;
  };

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchIdLS = getLS("branchId") || getLS("branchid");
  const branchLS = getLS("branchType") || getLS("branchtype");
  const userIdLS =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  // ✅ parsed values
  const branchId = useMemo(() => Number(branchIdLS || 0), [branchIdLS]);
  const branchType = useMemo(() => String(branchLS || "").trim(), [branchLS]);
  const userId = useMemo(() => String(userIdLS || "").trim(), [userIdLS]);

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  // ✅ edit id
  const qs = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const editTarInId =
    qs.get("TarInID") ||
    qs.get("tarInId") ||
    (typeof window !== "undefined" ? sessionStorage.getItem("TarInID") : "") ||
    "";

  const [outwards, setOutwards] = useState([]);
  const [productions, setProductions] = useState([]);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    inwardNo: "",
    date: todayISO(),
    outwardId: "0",
    productionId: "0",
    productionName: "",
    productionCode: "",
    projectName: "",
    workTypeName: "",
    availableQty: "0",
    recievedQty: "",
  });

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleBack = () => navigate(-1);

  const normalizeProductions = (list) => {
    return (list || []).map((x) => {
      const id =
        x?.ProductionId ??
        x?.ProductionID ??
        x?.productionId ??
        x?.productionid ??
        x?.fld_productionid ??
        x?.id ??
        0;

      const name =
        x?.ProductionName ??
        x?.productionName ??
        x?.productionname ??
        x?.fld_ProductionName ??
        x?.name ??
        "";

      const code =
        x?.ProductionCode ??
        x?.productionCode ??
        x?.productioncode ??
        x?.fld_ProductionCode ??
        x?.fld_Productioncode ??
        x?.code ??
        "";

      return {
        id: s(id) || "0",
        name: s(name) || "(No Name)",
        code: s(code),
      };
    });
  };

  const normalizeOutwards = (list) => {
    return (list || []).map((x) => {
      const id = x?.fld_ProdOutID ?? x?.ProdOutID ?? x?.outwardId ?? x?.id ?? 0;
      const code =
        x?.fld_outwardcode ?? x?.outwardcode ?? x?.OutwardCode ?? x?.code ?? "Select";

      return {
        id: s(id) || "0",
        code: s(code) || "Select",
      };
    });
  };

  const loadInit = async () => {
    try {
      setLoading(true);

      if (!branchId) {
        toast.error("branchId missing in localStorage");
        return;
      }

      const calls = [
        axios.get(`${API}/outwards`, {
          params: { branchId },
          headers: commonHeaders,
        }),
        axios.get(`${API}/productions`, {
          params: { branchId },
          headers: commonHeaders,
        }),
      ];

      if (!editTarInId) {
        calls.push(
          axios.get(`${API}/nextInwardNo`, {
            headers: commonHeaders,
          })
        );
      }

      const [outRes, prodRes, inRes] = await Promise.all(calls);

      const outList = normalizeOutwards(outRes.data?.list || []);
      const prodList = normalizeProductions(prodRes.data?.list || []);

      const outFinal = outList.some((x) => x.id === "0")
        ? outList
        : [{ id: "0", code: "Select" }, ...outList];

      const prodFinal = prodList.some((x) => x.id === "0")
        ? prodList
        : [{ id: "0", name: "--Select--", code: "" }, ...prodList];

      setOutwards(outFinal);
      setProductions(prodFinal);

      if (!editTarInId) {
        const inwardNo = s(inRes?.data?.inwardNo);
        setFormData((prev) => ({ ...prev, inwardNo }));
      } else {
        const editRes = await axios.get(`${API}/${editTarInId}`, {
          headers: commonHeaders,
        });

        const row = editRes.data?.data;

        if (row) {
          const pid =
            s(row.fld_productionid ?? row.ProductionID ?? row.ProductionId ?? "0") || "0";

          const pObj = prodFinal.find((x) => x.id === pid);

          setFormData((prev) => ({
            ...prev,
            inwardNo: s(row.fld_Inwardcode) || prev.inwardNo,
            outwardId: s(row.fld_outwardid ?? "0") || "0",
            productionId: pid,
            productionName: pObj?.name || s(row.fld_productionname) || "",
            productionCode: s(row.fld_productioncode) || pObj?.code || "",
            date: row.fld_date
              ? new Date(row.fld_date).toISOString().slice(0, 10)
              : prev.date,
            projectName: s(row.fld_projectname) || "",
            availableQty: s(row.Availableqty ?? row.availableQty ?? "0") || "0",
            recievedQty: s(row.Recievedqty ?? row.recievedQty ?? ""),
            workTypeName: s(row.fld_WorkTypeName ?? row.workTypeName ?? ""),
          }));
        }
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to load page");
    } finally {
      setLoading(false);
    }
  };

  const onOutwardChange = async (outwardId) => {
    const oid = s(outwardId) || "0";

    setFormData((prev) => ({ ...prev, outwardId: oid }));

    if (oid === "0") {
      setFormData((prev) => ({
        ...prev,
        outwardId: "0",
        productionId: "0",
        productionName: "",
        productionCode: "",
        projectName: "",
        availableQty: "0",
        recievedQty: "",
        workTypeName: "",
      }));
      return;
    }

    try {
      setLoading(true);

      const res = await axios.get(`${API}/outwardDetails`, {
        params: { outwardId: oid },
        headers: commonHeaders,
      });

      const d = res.data?.data;

      if (d) {
        const pid =
          s(d.fld_productionid ?? d.ProductionID ?? d.ProductionId ?? "0") || "0";

        const pObj = productions.find((x) => x.id === pid);

        setFormData((prev) => ({
          ...prev,
          outwardId: oid,
          productionId: pid,
          productionName: pObj?.name || s(d.ProductionName ?? d.productionname ?? ""),
          productionCode:
            s(
              d.fld_ProductionCode ??
                d.fld_Productioncode ??
                d.fld_productioncode ??
                d.ProductionCode ??
                d.productionCode ??
                d.productioncode
            ) ||
            prev.productionCode ||
            "",
          projectName: s(
            d.fld_ProjectName ??
              d.fld_projectname ??
              d.fld_contractname ??
              d.contractname ??
              ""
          ),
          availableQty: s(d.AvailableQty ?? d.availableQty ?? "0") || "0",
          recievedQty: s(
            d.TransferedQty ?? d.transferedQty ?? d.Recievedqty ?? d.recievedqty ?? ""
          ),
          workTypeName: s(d.fld_worktypename ?? d.fld_WorkTypeName ?? d.workTypeName ?? ""),
        }));
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to load outward details");
    } finally {
      setLoading(false);
    }
  };

  const onProductionChange = (productionId) => {
    const pid = s(productionId) || "0";
    const pObj = productions.find((x) => x.id === pid);

    setFormData((prev) => ({
      ...prev,
      productionId: pid,
      productionName: pObj?.name || "",
      productionCode: pObj?.code ? pObj.code : prev.productionCode,
    }));
  };

  useEffect(() => {
    if (!productions.length) return;
    if (!formData.productionId || formData.productionId === "0") return;

    const pObj = productions.find((x) => x.id === s(formData.productionId));
    if (!pObj) return;

    setFormData((prev) => ({
      ...prev,
      productionName: prev.productionName || pObj.name || "",
      productionCode: prev.productionCode || pObj.code || "",
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productions]);

  const handleSubmit = async () => {
    try {
      if (!formData.date) return toast.error("Please Enter Date");
      if (!formData.outwardId || formData.outwardId === "0") {
        return toast.error("Please Select Outward No");
      }
      if (!formData.productionId || formData.productionId === "0") {
        return toast.error("Please Select Production");
      }

      const rq = Number(formData.recievedQty || 0);
      const aq = Number(formData.availableQty || 0);

      if (!rq || rq <= 0) {
        return toast.error("Please Enter Transfer Qty Greater Than 0");
      }

      if (!branchId) return toast.error("branchId missing");
      if (!userId) return toast.error("userId missing");

      setLoading(true);

      const payload = {
        mode: editTarInId ? "E" : "N",
        tarInId: editTarInId ? Number(editTarInId) : 0,
        branchId: Number(branchId),
        userId,
        inwardCode: formData.inwardNo,
        outwardId: Number(formData.outwardId),
        date: formData.date,
        productionId: Number(formData.productionId),
        productionName: formData.productionName,
        productionCode: formData.productionCode,
        projectName: formData.projectName,
        workTypeName: formData.workTypeName,
        availableQty: Number(formData.availableQty || 0),
        recievedQty: Number(formData.recievedQty || 0),
      };

      const res = await axios.post(`${API}/save`, payload, {
        headers: commonHeaders,
      });

      if (res.data?.success) {
        toast.success(res.data?.message || "Saved Successfully");
        if (typeof window !== "undefined") {
          sessionStorage.removeItem("TarInID");
        }
        navigate("/OrderManagement/Tar_Inward_Listing");
      } else {
        toast.error(res.data?.message || "Not Saved Record");
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const inputClass =
    `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ` +
    (isDark
      ? "bg-gray-700 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900");

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  return (
    <ProjectPostOrderLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">PRODUCTION INWARD</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className="flex justify-start mb-4">
          <button
            onClick={handleBack}
          className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
            isDark
              ? "border-slate-600 text-slate-300 hover:bg-slate-800"
              : "border-slate-300 text-slate-700 hover:bg-slate-100"
          }`}
          >
            <FaArrowLeft /> Back
          </button>
        </div>

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark ? "bg-white/10 border-white/10 text-white" : "bg-white text-black"
          }`}
        >
          <div className="flex items-center justify-between mb-6">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Production Inward
            </h1>
            {loading && (
              <div className={`text-xs px-2 py-1 rounded ${isDark ? "bg-white/10" : "bg-gray-100"}`}>
                Loading...
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <div className="space-y-6">
              <div>
                <label className={labelClass}>Inward No</label>
                <input type="text" value={formData.inwardNo} className={inputClass} readOnly />
              </div>

              <div>
                <label className={labelClass}>
                  Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => handleInputChange("date", e.target.value)}
                  className={inputClass}
                />
              </div>

              <div>
                <label className={labelClass}>Project Name</label>
                <input
                  type="text"
                  value={formData.projectName}
                  onChange={(e) => handleInputChange("projectName", e.target.value)}
                  className={inputClass}
                />
              </div>

              <div>
                <label className={labelClass}>Production Code</label>
                <input
                  type="text"
                  value={formData.productionCode}
                  onChange={(e) => handleInputChange("productionCode", e.target.value)}
                  className={inputClass}
                />
              </div>

              <div>
                <label className={labelClass}>
                  Recieved Qty <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={formData.recievedQty}
                  onChange={(e) => handleInputChange("recievedQty", e.target.value)}
                  className={inputClass}
                />
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <label className={labelClass}>Outward No</label>
                <select
                  value={formData.outwardId}
                  onChange={(e) => onOutwardChange(e.target.value)}
                  className={inputClass}
                >
                  {outwards.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.code}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelClass}>
                  Production Name <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.productionId}
                  onChange={(e) => onProductionChange(e.target.value)}
                  className={inputClass}
                >
                  {productions.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelClass}>Selected Production Name</label>
                <input type="text" value={formData.productionName} className={inputClass} readOnly />
              </div>

              <div>
                <label className={labelClass}>Work Type Name</label>
                <input
                  type="text"
                  value={formData.workTypeName}
                  onChange={(e) => handleInputChange("workTypeName", e.target.value)}
                  className={inputClass}
                />
              </div>

              <div>
                <label className={labelClass}>Production Qty (Available Qty)</label>
                <input type="number" value={formData.availableQty} className={inputClass} readOnly />
              </div>

              <div className={`text-xs ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                * Selecting Outward No will auto-fill Project / Production / Qty / Work Type.
              </div>
            </div>
          </div>

          <div className="flex justify-center">
            <button
              onClick={handleSubmit}
              disabled={loading}
              className={`flex items-center gap-2 px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                loading ? "opacity-60 cursor-not-allowed" : ""
              } ${buttonClass} ${hoverClass} text-white`}
            >
              <FaPaperPlane /> {editTarInId ? "Update" : "Submit"}
            </button>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </ProjectPostOrderLayoutWrapper>
  );
}